import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';

import { Add } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { Backdrop, IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { useAppSelector } from 'state/hooks';
import { selectUser } from 'state/userSlice';

import SearchBar from 'features/common/SearchBar/SearchBar';
import { StlButton } from 'features/common/StlButton/StlButton';
import { StlToggle } from 'features/common/StlToggle/StlToggle';

import modalStyles from '../../StlModal/StlModal.module.css';
import styles from './StlViewAllModal.module.css';

export const FILTER = {
  ALL: 'all',
  MINE: 'mine',
} as const;

export type Filter = (typeof FILTER)[keyof typeof FILTER];

const getFilterOptions = (name: string): { value: Filter; label: string }[] => [
  { value: FILTER.ALL, label: `All ${name}` },
  { value: FILTER.MINE, label: `My ${name}` },
];

interface IViewAllModal {
  title: string;
  itemName: string;
  pluralItemName?: string;
  handleCreate: () => void;
  listComponent: React.ComponentType<any>;
  showCloseBtn?: boolean;
  onClose?: () => void;
  nameAccessor: string;
}
export default function StlViewAllModal({
  title,
  itemName,
  pluralItemName,
  handleCreate,
  listComponent,
  showCloseBtn = false,
  onClose,
  nameAccessor,
}: IViewAllModal) {
  const [filter, setFilter] = useState<Filter>(FILTER.ALL);
  const [searchTerm, setSearchTerm] = useState<string>(undefined);
  const user = useAppSelector(selectUser);
  const handleChange = (
    _e: React.MouseEvent<HTMLElement>,
    value: Filter | null,
  ) => {
    if (value !== null) setFilter(value);
  };

  const filters = useMemo(
    () => ({
      created_by: filter === FILTER.MINE ? user.email : undefined,
      [nameAccessor]: searchTerm,
    }),
    [searchTerm, filter],
  );

  const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value !== '')
      setSearchTerm(e.target.value.trim().toLowerCase());
    else setSearchTerm(undefined);
  }, []);

  const pluralName = pluralItemName ?? `${itemName}s`;
  const filteredOptions = useMemo(
    () => getFilterOptions(pluralName),
    [pluralName],
  );
  const ListToRender = listComponent;

  return (
    <Backdrop aria-labelledby="modal" className={modalStyles.backdrop} open>
      <Box className={styles.modal}>
        <div className={`${modalStyles.modal_content} ${styles.modal_content}`}>
          <Box className={styles.modal_header}>
            <Typography variant="h3" fontWeight={500}>
              {title}
            </Typography>
            {showCloseBtn && (
              <IconButton
                disableRipple
                onClick={() => onClose?.()}
                size="small"
                className={styles.close_icon}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
          <Box className={styles.modal_body}>
            <Box className={styles.modal_panel}>
              <Box display="flex" justifyContent="space-between">
                <Box className={styles.toggle_wrapper}>
                  <StlToggle
                    value={filter}
                    onChange={handleChange}
                    ariaLabel={`Filter ${pluralName}`}
                    options={filteredOptions}
                  />
                </Box>
                <StlButton
                  isActive
                  value={`Create New ${itemName}`}
                  handleClick={handleCreate}
                  icon={<Add />}
                  width={175}
                  bold
                />
              </Box>
              <Box className={styles.search_wrapper}>
                <SearchBar
                  expanded
                  placeholder={`Search ${pluralName}`}
                  searchTerm={searchTerm}
                  onChange={handleSearch}
                  iconPos="left"
                  iconCol="#8a8a8a"
                />
              </Box>
            </Box>
            <Box className={styles.modal_list}>
              <ListToRender filters={filters} wide />
            </Box>
          </Box>
        </div>
      </Box>
    </Backdrop>
  );
}
